import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { api } from "../../axios/api";
import { Button, Form, Table, Card, Spinner } from "react-bootstrap";
import { ArrowLeft, Trash2, FileText, ShieldAlert } from "lucide-react";

// Componente de feedback de sucesso
import FaturaSucessoModal from "../../components/FaturaSucesso";

const NotaCredito = () => {
  const { id } = useParams(); // ID da Fatura Original
  const navigate = useNavigate();
  
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;

  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [faturaOriginal, setFaturaOriginal] = useState(null);
  const [itensOriginais, setItensOriginais] = useState([]);
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [faturaGeradaId, setFaturaGeradaId] = useState(null);
  
  const [documentoData, setDocumentoData] = useState({
    empresa_id: empresaId,
    user_id: user?.id,
    doc_type: "NC",
    cliente_id: null,
    itens: [], 
    observacoes: "", 
    data_emissao: new Date().toISOString().split("T")[0],
    total_liquido: 0,
    total_iva: 0,
    total_geral: 0,
    total_retencao: 0,
    metodo_pagamento: "OS",
  });

  useEffect(() => {
    if (id && empresaId) {
      fetchDadosIniciais();
    }
  }, [id, empresaId]);

  const fetchDadosIniciais = async () => {
    try {
      setLoading(true);
      const resDoc = await api.get(`/app/documentos/${id}`);
      const fatura = resDoc.data;
      
      if (!fatura) {
        Swal.fire("Erro", "Fatura não encontrada.", "error");
        return navigate(-1);
      }

      setFaturaOriginal(fatura);

      // Tratamento para o problema do "0.00" no seu JSON
      const itensMapeados = (fatura.DocumentoItems || []).map((item, index) => {
        const qtd = parseFloat(item.qtd || 0);
        const preco = parseFloat(item.preco_unitario || 0);
        const taxaIva = parseFloat(item.taxa_iva || item.iva_percentagem || 0);
        
        // Recálculo manual para evitar o erro de dados zerados vindos do banco
        const subtotalLiq = qtd * preco;
        const totalFinal = subtotalLiq + (subtotalLiq * (taxaIva / 100));

        return {
          produto_id: item.produto_id,
          nome: item.Produto?.nome || item.nome || "Produto",
          qtd: qtd,
          preco_unitario: preco,
          taxa_iva: taxaIva,
          desconto_valor: parseFloat(item.desconto_valor || 0),
          total_item_final: totalFinal,
          referenceItemLineNo: index + 1 // ID da linha na fatura original
        };
      });

      setItensOriginais(JSON.parse(JSON.stringify(itensMapeados)));

      setDocumentoData(prev => ({
        ...prev,
        cliente_id: fatura.cliente_id,
        itens: itensMapeados,
        total_retencao: parseFloat(fatura.retencao_fonte || 0)
      }));
      
      calculateTotals(itensMapeados);
    } catch (error) {
      Swal.fire("Erro", "Falha ao carregar dados da fatura.", "error");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (itens) => {
    const liq = itens.reduce((acc, cur) => acc + (cur.qtd * cur.preco_unitario), 0);
    const iva = itens.reduce((acc, cur) => acc + (cur.qtd * cur.preco_unitario * (cur.taxa_iva / 100)), 0);
    setDocumentoData(prev => ({ 
        ...prev, 
        total_liquido: liq, 
        total_iva: iva, 
        total_geral: liq + iva 
    }));
  };

  const updateItem = (index, field, value) => {
    const val = parseFloat(value || 0);
    const itemReferencia = itensOriginais[index];
    const novosItens = [...documentoData.itens];

    if (field === 'qtd') {
      if (val > itemReferencia.qtd) {
        Swal.fire("Limite Excedido", `Máximo permitido: ${itemReferencia.qtd}`, "warning");
        novosItens[index].qtd = itemReferencia.qtd;
      } else {
        novosItens[index].qtd = val;
      }
    }

    const subLiq = novosItens[index].qtd * novosItens[index].preco_unitario;
    novosItens[index].total_item_final = subLiq + (subLiq * (novosItens[index].taxa_iva / 100));

    setDocumentoData(prev => ({ ...prev, itens: novosItens }));
    calculateTotals(novosItens);
  };

  const handleFinalizar = async () => {
    // Validação obrigatória para AGT
    if (!documentoData.observacoes || documentoData.observacoes.trim().length < 5) {
      return Swal.fire("Atenção", "Por favor, indique o motivo da retificação conforme exigido pela AGT.", "warning");
    }

    if (documentoData.itens.length === 0) {
      return Swal.fire("Atenção", "A nota de crédito deve conter pelo menos um item.", "warning");
    }

    setIsProcessing(true);
    try {
      // PAYLOAD ALINHADO AO SEU ENDPOINT E MÉTODO createNotaCredito (Sem referências a séries)
      const payload = {
        empresa_id: documentoData.empresa_id,
        cliente_id: documentoData.cliente_id,
        user_id: documentoData.user_id,
        doc_type: "NC",
        data_emissao: documentoData.data_emissao,
        total_retencao: documentoData.total_retencao,
        numero_fatura_origem: faturaOriginal?.numero, // Crucial para o back-end localizar e anular o doc original
        motivo_retificacao: documentoData.observacoes,
        itens: documentoData.itens.map(item => ({
          produto_id: item.produto_id,
          nome: item.nome,
          qtd: item.qtd,
          preco_unitario: item.preco_unitario,
          iva_percentagem: item.taxa_iva,
          desconto_valor: item.desconto_valor,
          observacoes: item.observacoes || ""
        }))
      };

      const res = await api.post("/app/documentos/create/nota-credito/", payload);
      
      if (res.data.success) {
        setFaturaGeradaId(res.data.id); 
        setShowSuccessModal(true);
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Erro na Emissão", e.response?.data?.error || "Erro ao comunicar com o servidor.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) return <div className="p-5 text-center"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f4f7f9' }}>
      
      <style>{`
        .win10-btn { border-radius: 0px !important; }
        .win10-input { border-radius: 0px !important; border: 1px solid #ced4da !important; font-size: 14px; }
        .win10-card { border-radius: 0px !important; border: 1px solid #d1d1d1 !important; box-shadow: none !important; }
        .win10-header { background: #fff; border-bottom: 1px solid #d1d1d1; padding: 12px 25px; }
        .nc-total-box { background: #d83b01; color: #fff; border-radius: 0px; }
      `}</style>

      <header className="win10-header">
        <div className="container-fluid">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <Button variant="light" className="win10-btn border" onClick={() => navigate(-1)}>
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h5 className="mb-0 fw-bold">Emitir Nota de Crédito</h5>
                <small className="text-muted">Retificando e Anulando: <span className="text-primary fw-bold">{faturaOriginal?.numero}</span></small>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
        <div className="container-fluid px-1">
          <div className="row g-4">
            <div className="col-lg-8">
              <Card className="win10-card">
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr className="small text-uppercase text-muted">
                        <th className="ps-4 py-3">Descrição</th>
                        <th className="text-center">Orig.</th>
                        <th className="text-center" style={{width: '120px'}}>Qtd. a Devolver</th>
                        <th className="text-center">Preço Unit.</th>
                        <th className="text-end pe-4">Subtotal</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentoData.itens.map((item, index) => (
                        <tr key={index}>
                          <td className="ps-4">
                            <div className="fw-bold">{item.nome}</div>
                            <small className="text-primary">IVA: {item.taxa_iva}%</small>
                          </td>
                          <td className="text-center text-muted">{itensOriginais[index]?.qtd}</td>
                          <td>
                            <Form.Control 
                              type="number" 
                              size="sm" 
                              className="win10-input text-center" 
                              value={item.qtd} 
                              onChange={(e) => updateItem(index, 'qtd', e.target.value)} 
                            />
                          </td>
                          <td className="text-center">
                            {item.preco_unitario.toLocaleString('pt-AO', {minimumFractionDigits: 2})}
                          </td>
                          <td className="text-end pe-4 fw-bold">
                            {item.total_item_final.toLocaleString('pt-AO', {minimumFractionDigits: 2})}
                          </td>
                          <td className="text-center">
                            <Button variant="link" className="text-danger p-0" onClick={() => {
                                const filtrados = documentoData.itens.filter((_, i) => i !== index);
                                setDocumentoData({...documentoData, itens: filtrados});
                                calculateTotals(filtrados);
                            }}>
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </div>

            <div className="col-lg-4">
              <Card className="win10-card mb-4">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-3 text-primary fw-bold">
                    <FileText size={18} />
                    <span className="small text-uppercase">Motivo da Retificação (AGT)</span>
                  </div>
                  <Form.Control 
                    as="textarea" rows={4} className="win10-input" 
                    placeholder="Descreva o motivo legal para esta nota de crédito..."
                    value={documentoData.observacoes} 
                    onChange={(e) => setDocumentoData(prev => ({...prev, observacoes: e.target.value}))}
                  />
                  <div className="mt-2 x-small text-muted" style={{fontSize: '11px'}}>
                    * Obrigatório para conformidade com o SAF-T (AO).
                  </div>
                </Card.Body>
              </Card>

              <Card className="win10-card border-0 nc-total-box shadow">
                <Card.Body className="p-4 text-center">
                  <span className="d-block opacity-75 small mb-2">TOTAL A CREDITAR</span>
                  <h2 className="fw-bold mb-4">
                    {documentoData.total_geral.toLocaleString('pt-AO', {minimumFractionDigits: 2})} 
                    <span className="ms-2 small" style={{fontSize: '14px'}}>Kz</span>
                  </h2>
                  
                  <Button 
                    className="win10-btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 border-0" 
                    onClick={handleFinalizar} 
                    disabled={isProcessing || documentoData.total_geral <= 0 || !documentoData.observacoes} 
                    style={{ backgroundColor: '#fff', color: '#d83b01' }}
                  >
                    {isProcessing ? <Spinner size="sm" animation="border" /> : <ShieldAlert size={20} />}
                    {isProcessing ? "A PROCESSAR NA AGT..." : "FINALIZAR NOTA DE CRÉDITO"}
                  </Button>
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {showSuccessModal && (
        <FaturaSucessoModal 
          faturaId={faturaGeradaId} 
          onClose={() => {
            setShowSuccessModal(false);
            navigate('/documentos'); 
          }} 
        />
      )}
    </div>
  );
};

export default NotaCredito;